import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { type User } from '../../auth';
import { queryKeys } from '../../lib/queryKeys';
import { type AppState } from '../../reducer';
import { Loading, LoadingError, type RequestError, ResourceNotFound } from '../../shared';
import { useAuth } from '../../shared/hooks/useAuth';
import { useSettings } from '../../shared/hooks/useSettings';
import { Actions } from '../actions';
import { getAuditLogEntryDetailUrl } from '../api';
import type { CustomRoleResponse, MemberSessionsResponse } from '../api';
import { AUDIT_LOG_TAB_ID } from '../auditLogLinks';
import { AccountDetails } from '../components';
import { useAccount } from '../hooks/useAccount';
import { useAccountSubscription } from '../hooks/useAccountSubscription';
import { useAccountSubscriptionUsage } from '../hooks/useAccountSubscriptionUsage';
import { useAuditLogEntries } from '../hooks/useAuditLogEntries';
import { useAuditLogEntryDetail } from '../hooks/useAuditLogEntryDetail';
import { useCustomRoles } from '../hooks/useCustomRoles';
import { useDisableSso } from '../hooks/useDisableSso';
import { useLegacySubscription } from '../hooks/useLegacySubscription';
import { useMembers } from '../hooks/useMembers';
import { useMemberSessions } from '../hooks/useMemberSessions';
import {
  type Account,
  type CustomRole,
  type Member,
  type MemberSessions,
  type Plan,
  PlanType,
} from '../model';

export function AccountPageContainer() {
  const { accountId } = useParams<{ accountId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const settings = useSettings();
  const { scopes, user } = useAuth();
  const isUpdatingSubscription = Boolean(
    useSelector((s: AppState) => s.accounts.subscriptionUpdateRequest)?.isFetching,
  );
  const subscriptionReq = useSelector((s: AppState) => s.accounts.subscriptionUpdateRequest) as
    | { isFetching?: boolean; error?: RequestError }
    | undefined;
  const subscriptionUpdateError: RequestError | null = subscriptionReq?.error || null;
  const previousIsUpdatingSubscriptionRef = useRef(isUpdatingSubscription);

  // Fetch core account entity. Pass '' when the route has no accountId so the
  // query stays disabled; the missing-id early return below runs only after
  // every hook has been called, keeping hook order stable (Rules of Hooks).
  const accountQuery = useAccount(accountId ?? '');
  const account: Account | undefined = accountQuery.data?.entities?.accounts?.[accountId ?? ''];

  const resolvedAccountId = account?._id;
  const accountRef = useRef(account);
  useEffect(() => {
    accountRef.current = account;
  }, [account]);

  // Disable SSO is a react-query mutation (no Redux): on success it writes the
  // server's updated SAML config straight into the account cache, so the panel
  // re-renders with SSO disabled.
  const disableSso = useDisableSso(account ?? null);

  // Dependent resources
  const subscriptionQuery = useAccountSubscription(account || null);
  const subscription = subscriptionQuery.data;

  const subscriptionUsageQuery = useAccountSubscriptionUsage(scopes.accountUsage ? account || null : null);
  const subscriptionUsage = subscriptionUsageQuery.data ?? undefined;

  const legacySubscriptionQuery = useLegacySubscription(
    subscription?.planType === PlanType.LEGACY ? account || null : null,
  );
  const legacySubscription = legacySubscriptionQuery.data ?? null;

  const memberSessionsResp = useMemberSessions(settings.enableMemberSessions ? account || null : null)
    .data as MemberSessionsResponse | null;
  const memberSessions: MemberSessions[] = memberSessionsResp?.entities?.memberSessions
    ? Object.values(memberSessionsResp.entities.memberSessions)
    : [];


  const customRolesResp = useCustomRoles(account || null).data as CustomRoleResponse | null;
  const customRoles: { [id: string]: CustomRole } = customRolesResp?.entities?.customRoles || {};
  const { enableVitalAuditLogEnhancedSearch } = settings;
  const auditMembersQuery = useMembers(scopes.auditLog ? account || null : null, {
    paginated: false,
    enabled: Boolean(account) && scopes.auditLog && enableVitalAuditLogEnhancedSearch,
  });
  const auditMembers: Member[] = auditMembersQuery.data?.entities?.members
    ? (Object.values(auditMembersQuery.data.entities.members) as Member[]).sort((a, b) =>
        a.email.localeCompare(b.email),
      )
    : [];

  // Invalidate subscription cache when subscription update completes successfully
  useEffect(() => {
    const wasUpdating = previousIsUpdatingSubscriptionRef.current;

    // If we were updating before and now we're not, and there's no error, the update succeeded
    if (wasUpdating && !isUpdatingSubscription && !subscriptionUpdateError && resolvedAccountId) {
      // Invalidate subscription-related queries for this account to force refetch with updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.subscription(resolvedAccountId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.subscriptionUsage(resolvedAccountId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.legacySubscription(resolvedAccountId),
      });
    }

    // Update the ref for the next render
    previousIsUpdatingSubscriptionRef.current = isUpdatingSubscription;
  }, [isUpdatingSubscription, subscriptionUpdateError, resolvedAccountId, queryClient]);

  // Fetch EAP segments when account is available
  useEffect(() => {
    const currentAccount = accountRef.current;
    if (
      currentAccount &&
      currentAccount._id === resolvedAccountId &&
      settings.enableGeneralTab &&
      process.env.REACT_APP_EAP_SEGMENTS_API_KEY &&
      process.env.REACT_APP_EAP_SEGMENTS_API_URL
    ) {
      dispatch(Actions.fetchEapSegments(currentAccount));
    }
  }, [resolvedAccountId, dispatch, settings.enableGeneralTab]);

  // Audit log state and hooks
  const [auditParams, setAuditParams] = useState<{
    query?: string;
    spec?: string;
    member?: string;
    fuzzy?: boolean;
    date?: ReadonlyArray<Date | null>;
    actions?: readonly string[];
  }>({});
  const [localSelectedEntryId, setLocalSelectedEntryId] = useState<string | null>(null);
  const linksEnabled = Boolean(settings.enableAuditLogEntryLinks);
  const selectedEntryId = linksEnabled ? searchParams.get('entry') : localSelectedEntryId;
  // When a deep-linked entry is not on the loaded page, this is `entry.date + 1`.
  // The list API returns entries that occurred before that timestamp, so one
  // request is the page that contains the entry.
  const [listBefore, setListBefore] = useState<number | undefined>(undefined);
  const [prevAuditParams, setPrevAuditParams] = useState(auditParams);
  if (
    prevAuditParams.query !== auditParams.query ||
    prevAuditParams.spec !== auditParams.spec ||
    prevAuditParams.member !== auditParams.member ||
    prevAuditParams.fuzzy !== auditParams.fuzzy ||
    prevAuditParams.date !== auditParams.date ||
    prevAuditParams.actions !== auditParams.actions
  ) {
    setPrevAuditParams(auditParams);
    setListBefore(undefined);
  }
  const auditEntriesQuery = useAuditLogEntries(
    scopes.auditLog ? account || null : null,
    enableVitalAuditLogEnhancedSearch
      ? { ...auditParams, beforeCursor: listBefore }
      : {
          query: auditParams.query,
          spec: auditParams.spec,
          date: auditParams.date,
          beforeCursor: listBefore,
        },
  );

  // Resolve the entry detail URL: prefer a loaded entry's self href, else build
  // it from the id so entries that are not on the current page still resolve.
  const loadedAuditEntries = auditEntriesQuery.data?.entries ?? [];
  const matchedLoadedEntry = selectedEntryId
    ? loadedAuditEntries.find((entry) => entry?._id === selectedEntryId)
    : undefined;
  const auditDetailUrl = selectedEntryId
    ? (matchedLoadedEntry?._links?.self?.href ?? getAuditLogEntryDetailUrl(selectedEntryId))
    : null;

  const auditEntryDetailQuery = useAuditLogEntryDetail(scopes.auditLog ? account || null : null, auditDetailUrl);

  const anchoredEntryDate =
    auditEntryDetailQuery.data?._id === selectedEntryId ? auditEntryDetailQuery.data.date : undefined;
  const [anchoredForEntry, setAnchoredForEntry] = useState<string | null>(null);
  // Set while "Back to newest" is in flight. Closing the entry is a navigation
  // and lands in a later commit than the window reset, so for one render the
  // entry still looks open with no window — exactly what re-anchors the list.
  const [skipAnchor, setSkipAnchor] = useState(false);

  if (selectedEntryId && anchoredForEntry !== selectedEntryId) {
    setAnchoredForEntry(selectedEntryId);
    const loaded = auditEntriesQuery.data?.entries ?? [];
    if (!loaded.some((entry) => entry?._id === selectedEntryId)) {
      setListBefore(undefined);
    }
  }

  const auditSearchKey = [
    auditParams.query ?? '',
    auditParams.spec ?? '',
    auditParams.member ?? '',
    auditParams.fuzzy ? '1' : '0',
    auditParams.date?.[0]?.getTime() ?? '',
    auditParams.date?.[1]?.getTime() ?? '',
    (auditParams.actions ?? []).join(','),
  ].join('\0');
  const [prevAuditSearchKey, setPrevAuditSearchKey] = useState(auditSearchKey);
  if (auditSearchKey !== prevAuditSearchKey) {
    setPrevAuditSearchKey(auditSearchKey);
    setListBefore(undefined);
  }

  const loadedForAnchor = auditEntriesQuery.data?.entries ?? [];
  const entryIsLoaded = selectedEntryId
    ? loadedForAnchor.some((entry) => entry?._id === selectedEntryId)
    : false;
  if (
    !skipAnchor &&
    linksEnabled &&
    selectedEntryId &&
    anchoredEntryDate !== undefined &&
    listBefore === undefined &&
    !auditParams.date?.[0] &&
    !auditParams.date?.[1] &&
    !auditEntriesQuery.isLoading &&
    !auditEntriesQuery.isFetching &&
    !entryIsLoaded
  ) {
    setListBefore(anchoredEntryDate + 1);
  }

  // After the anchor adjustment, so the suppression survives the render where the
  // entry is still open, and lifts once it has actually closed. A later link to
  // the same entry anchors normally again.
  if (!selectedEntryId && skipAnchor) {
    setSkipAnchor(false);
  }

  // All hooks have now run, so it is safe to bail out on a missing route param.
  if (!accountId) {
    return <div>No account ID provided</div>;
  }

  const isFetching =
    accountQuery.isLoading ||
    subscriptionQuery.isLoading ||
    subscriptionUsageQuery.isLoading ||
    (subscription?.planType === PlanType.LEGACY && legacySubscriptionQuery.isLoading);

  if (isFetching) {
    return <Loading title={`Loading ${accountId}`} />;
  }

  // Only a 404 from the account query itself means the account does not exist.
  // A dependent resource 404ing used to render "Account not found" too, which
  // sent operators looking for a missing account instead of a missing
  // subscription.
  if (accountQuery.error) {
    if (accountQuery.error.status === 404) {
      return (
        <ResourceNotFound
          title="Account not found"
          description={
            <p>
              <code>{accountId}</code> was not found
            </p>
          }
          returnTo="/accounts"
          returnLabel="Go back to accounts"
        />
      );
    }
    return <LoadingError title={`Failed to load account ${accountId}`} />;
  }

  if (subscriptionQuery.error) {
    return <LoadingError title={`Failed to load subscription for account ${accountId}`} />;
  }

  if (legacySubscriptionQuery.error) {
    return <LoadingError title={`Failed to load legacy subscription for account ${accountId}`} />;
  }

  if (!account || !subscription) {
    return <LoadingError title={`Failed to load account ${accountId}`} />;
  }

  const handleChangeLegacyTrialEnd = (date: Date) =>
    legacySubscription && dispatch(Actions.updateLegacyTrialEnd(account, legacySubscription, date));
  const handleChangeLegacyGraceEnd = (date: Date) =>
    legacySubscription && dispatch(Actions.updateLegacyGraceEnd(account, legacySubscription, date));
  const handleChangeTrialEnd = (date: Date) =>
    subscription && dispatch(Actions.updateTrialEnd(account, subscription, date));
  const handleChangeGraceEnd = (date: Date) =>
    subscription && dispatch(Actions.updateGraceEnd(account, subscription, date));
  const handleChangeEnterpriseCampaignDates = (date: Date) =>
    subscription && dispatch(Actions.updateEnterpriseCampaignDates(account, subscription, date));
  const handleChangeGuardianCampaignDates = (date: Date) =>
    subscription && dispatch(Actions.updateGuardianCampaignDates(account, subscription, date));
  const handleSubscribe = (plan: Plan) => subscription && dispatch(Actions.subscribe(account, subscription, plan));
  const handleCancelSubscription = (acc: Account, endImmediately: boolean) =>
    dispatch(Actions.cancelSubscription(acc, endImmediately));
  const handleSelectEntry = (entryId: string) => {
    if (linksEnabled) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', AUDIT_LOG_TAB_ID);
        next.set('entry', entryId);
        return next;
      });
    } else {
      setLocalSelectedEntryId(entryId);
    }
  };

  const handleCloseEntryDetails = () => {
    if (linksEnabled) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('entry');
          return next;
        },
        { replace: true },
      );
    } else {
      setLocalSelectedEntryId(null);
    }
  };

  // Jump straight to the newest entries. One request, versus a round trip per
  // 20 rows to bridge the gap. The open entry has to close too, otherwise the
  // list re-anchors right back to it.
  const handleBackToNewest = () => {
    // Only worth suppressing when there is an entry to close, since that is the
    // render being guarded against. Setting it with nothing open would leave it
    // set, because the clear runs only when the open entry changes, and the next
    // link to an off-screen entry would then open
    // without the list moving to it.
    setSkipAnchor(Boolean(selectedEntryId));
    handleCloseEntryDetails();
    setListBefore(undefined);
  };

  const activeTab = linksEnabled ? (searchParams.get('tab') ?? undefined) : undefined;
  // Leaving the audit log closes whatever entry was open. The selection lives
  // on the page so a link can open it, so it outlives the panel unless it is
  // cleared here. This has to run on both flag paths.
  const handleTabChange = (tabId: string) => {
    if (!linksEnabled) {
      if (tabId !== AUDIT_LOG_TAB_ID) {
        setLocalSelectedEntryId(null);
      }
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      if (tabId !== AUDIT_LOG_TAB_ID) {
        next.delete('entry');
      }
      return next;
    });
  };

  return (
    <AccountDetails
      settings={settings}
      scopes={scopes}
      account={account}
      subscriptionUsage={subscriptionUsage}
      subscriptionUsageLoading={subscriptionUsageQuery.isLoading}
      subscriptionUsageError={subscriptionUsageQuery.error ?? undefined}
      legacySubscription={legacySubscription}
      subscription={subscription}
      isUpdatingSubscription={isUpdatingSubscription}
      memberSessions={memberSessions}
      customRoles={customRoles}
      auditLogEntries={auditEntriesQuery.data}
      auditLogEntriesLoading={auditEntriesQuery.isLoading}
      auditLogEntriesError={auditEntriesQuery.error as RequestError | null}
      entryDetails={auditEntryDetailQuery.data}
      entryDetailsLoading={auditEntryDetailQuery.isLoading}
      entryDetailsError={auditEntryDetailQuery.error}
      currentAuditQuery={auditParams.query}
      currentAuditSpec={auditParams.spec}
      currentAuditMember={enableVitalAuditLogEnhancedSearch ? auditParams.member : undefined}
      currentAuditFuzzy={enableVitalAuditLogEnhancedSearch ? auditParams.fuzzy : undefined}
      currentAuditDate={auditParams.date}
      currentAuditActions={enableVitalAuditLogEnhancedSearch ? auditParams.actions : undefined}
      auditLogMembers={auditMembers}
      auditLogMembersLoading={auditMembersQuery.isLoading}
      enableVitalAuditLogEnhancedSearch={enableVitalAuditLogEnhancedSearch}
      user={user as User}
      onChangeTrialEnd={handleChangeTrialEnd}
      onChangeGraceEnd={handleChangeGraceEnd}
      onChangeLegacyTrialEnd={handleChangeLegacyTrialEnd}
      onChangeLegacyGraceEnd={handleChangeLegacyGraceEnd}
      onSubscribe={handleSubscribe}
      onCancelSubscription={handleCancelSubscription}
      onSearchParamsChange={({ query, spec, member, fuzzy, date, actions }) =>
        setAuditParams({
          query,
          spec,
          member: enableVitalAuditLogEnhancedSearch ? member : undefined,
          fuzzy: enableVitalAuditLogEnhancedSearch ? fuzzy : undefined,
          date: (date || []) as ReadonlyArray<Date | null>,
          actions: enableVitalAuditLogEnhancedSearch ? actions : undefined,
        })
      }
      onSelectEntry={handleSelectEntry}
      onCloseEntryDetails={handleCloseEntryDetails}
      selectedEntryId={selectedEntryId}
      selectedEntryUrl={auditDetailUrl}
      enableAuditLogEntryLinks={linksEnabled}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      onBackToNewest={handleBackToNewest}
      onChangeEnterpriseCampaignDates={handleChangeEnterpriseCampaignDates}
      onChangeGuardianCampaignDates={handleChangeGuardianCampaignDates}
      onSetMfaDisabled={(acct, member) => dispatch(Actions.setMfaDisabled(acct, member))}
      setSsoDisabled={() => disableSso.mutate()}
      isDisablingSso={disableSso.isPending}
    />
  );
}
